import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { User } from "../app/api.js";
import { api } from "../app/api.js";
import { authApi } from "../api/auth.js";

type SessionValue = {
  ready: boolean;
  user: User | null;
  setUser: (user: User | null) => void;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children, initialUser, skipInitialLoad = false }: { children: ReactNode; initialUser?: User | null; skipInitialLoad?: boolean }) {
  const [ready, setReady] = useState(skipInitialLoad);
  const [user, setUser] = useState<User | null>(initialUser ?? null);

  const refresh = useCallback(async () => {
    const workspacePreload = api.preloadOrganizations();
    void workspacePreload.catch(() => undefined);
    try {
      const result = await authApi.session();
      setUser(result.user);
      if (!result.user) api.clearOrganizationPreload();
    } catch {
      setUser(null);
      api.clearOrganizationPreload();
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    if (!skipInitialLoad) void refresh();
  }, [refresh, skipInitialLoad]);

  const logout = useCallback(async () => {
    await authApi.logout();
    api.clearOrganizationPreload();
    setUser(null);
  }, []);

  const value = useMemo(() => ({ ready, user, setUser, refresh, logout }), [ready, user, refresh, logout]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used within SessionProvider");
  return value;
}
